import { Theme } from "@mui/material/styles";
import React, { Dispatch, ReactNode, SetStateAction } from "react";
import {
  GrantsFiltersType,
  GrantsPagination,
  SummaryGrant,
} from "../../slices/grants";
import { RequestStatus } from "../../utils/Helpers/fetchStatus";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  createFilterOptions,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Button from "@mui/material/Button";
import { FiltersIcon } from "../../components/Icons/FiltersIcon";
import { SourceIcon } from "../../components/Icons/SourceIcon";
import { LastUpdatedIcon } from "../../components/Icons/LastUpdatedIcon";
import { TableProperties } from "exceljs";
import {
  buildExcelTable,
  genEmptyRow,
  getChunksFromString,
  MaxCharsAllowed,
  getOrderedExportColumns,
  mapColumnsToExportFormat,
} from "../../services/exporter";
import { removeHtmlTags } from "../../services/dataParser";
import { DateFormat, formatDate } from "../../utils/conversion/date-converters";
import { ForecastRecord } from "../../utils/types/Forecast";
import axios from "axios";
import {
  createRequestWithAuthHeaders,
  getFullUrl,
} from "../../configs/axios-export.custom";
import { buildQuery } from "../../utils/Helpers/queryBuilder";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  ColumnsSettingsData,
  DataColumnConfigProps,
} from "../../components/MaterialTable/Table";
import { getGrantsColumnsConfig } from "./columns.config";

export const getSortableColumnPropertyName = (
  _propertyName: string,
): string => {
  return _propertyName === "lastUpdatedDateIn"
    ? "lastUpdatedDate"
    : _propertyName === "secondaryContactInfo"
      ? "pointOfContactTwo"
      : _propertyName;
};

const isNumeric = (str: any) => {
  return !isNaN(str) && !isNaN(parseFloat(str));
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

type AgencyFilterKey =
  | "includeUsaid"
  | "includeMcc"
  | "includeStateDept"
  | "includeCdc";

const AGENCY_OPTIONS: ReadonlyArray<{
  key: AgencyFilterKey;
  label: string;
  fullName: string;
}> = [
  {
    key: "includeUsaid",
    label: "USAID",
    fullName: "U.S. Agency for International Development",
  },
  {
    key: "includeMcc",
    label: "MCC",
    fullName: "Millennium Challenge Corporation",
  },
  {
    key: "includeStateDept",
    label: "State Dept",
    fullName: "Department of State",
  },
  {
    key: "includeCdc",
    label: "CDC",
    fullName: "Centers for Disease Control and Prevention",
  },
];

const agencyFilterOptions = createFilterOptions<(typeof AGENCY_OPTIONS)[number]>(
  {
    stringify: (opt) => `${opt.label} ${opt.fullName}`,
  },
);

export const getTableHeader = (
  theme: Theme,
  props: {
    handleOpenFilters: () => void;
    setFilters: Dispatch<SetStateAction<any>>;
    lastUpdated: string | null;
    fetchStatus: string;
    inputValue: string;
    onChange: (event) => void;
    // extendedFilters: ForecastExtendedFiltersProps;
    allFilters: {
      pagination: GrantsPagination;
      filters: GrantsFiltersType;
      sortOption: {
        sortField: string;
        sortOrder: number;
      };
    };
  },
): Array<ReactNode> => {
  const {
    handleOpenFilters,
    setFilters,
    lastUpdated,
    fetchStatus,
    inputValue,
    onChange,
    // extendedFilters,
    allFilters: { pagination, filters, sortOption },
  } = props;

  const isDataFetching = RequestStatus.isFetching(fetchStatus);

  const extendedFiltersLength = Object.entries(filters)
    ?.filter(
      ([key, value]) =>
        !key.includes("include") &&
        !key.includes("sort") &&
        key !== "onlyUntrackedInPipeline",
    )
    ?.filter(([key, value]) =>
      Array.isArray(value) ? value.length > 0 : !!value,
    )
    ?.map(([key, value]) => key)?.length;

  const topLine = (
    <Box
      sx={{
        pt: 1,
        display: "flex",
        gap: "16px",
        alignItems: "center",
      }}
    >
      {/* todo: temporarily hidden, restore and adjust when possible */}
      {/*<TextField*/}
      {/*  value={inputValue}*/}
      {/*  onChange={onChange}*/}
      {/*  sx={{*/}
      {/*    width: "300px",*/}
      {/*  }}*/}
      {/*  InputProps={{*/}
      {/*    startAdornment: (*/}
      {/*      <SearchIcon sx={{ color: theme.palette.action.active }} />*/}
      {/*    ),*/}
      {/*    endAdornment: (*/}
      {/*      <>*/}
      {/*        {inputValue?.length > 0 && (*/}
      {/*          <IconButton*/}
      {/*            onClick={() => onChange({ target: { value: "" } })}*/}
      {/*            sx={{ mr: 0.5 }}*/}
      {/*            disabled={isDataFetching}*/}
      {/*          >*/}
      {/*            <ClearIcon />*/}
      {/*          </IconButton>*/}
      {/*        )}*/}
      {/*        <Box*/}
      {/*          sx={{*/}
      {/*            borderLeft: `solid 1px ${theme.palette.divider}`,*/}
      {/*            pl: 0.5,*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          <Tooltip title={"Filters"}>*/}
      {/*            <span>*/}
      {/*              <IconButton onClick={handleOpenFilters}>*/}
      {/*                <FiltersIcon sx={{ width: "24px", height: "24px" }} />*/}
      {/*              </IconButton>*/}
      {/*              {extendedFiltersLength > 0 && (*/}
      {/*                <Avatar*/}
      {/*                  sx={{*/}
      {/*                    position: "absolute",*/}
      {/*                    height: "16px",*/}
      {/*                    width: "16px",*/}
      {/*                    top: "2px",*/}
      {/*                    right: "2px",*/}
      {/*                    fontSize: "12px",*/}
      {/*                    backgroundColor: theme.palette.secondary.main,*/}
      {/*                    // border: `solid 1px ${theme.palette.secondary.main}`,*/}
      {/*                    pointerEvents: "none",*/}
      {/*                  }}*/}
      {/*                >*/}
      {/*                  {extendedFiltersLength}*/}
      {/*                </Avatar>*/}
      {/*              )}*/}
      {/*            </span>*/}
      {/*          </Tooltip>*/}
      {/*        </Box>*/}
      {/*      </>*/}
      {/*    ),*/}
      {/*    sx: {*/}
      {/*      px: 0.5,*/}
      {/*    },*/}
      {/*  }}*/}
      {/*  // inputProps={{*/}
      {/*  //   style: {*/}
      {/*  //     padding: "8px",*/}
      {/*  //   },*/}
      {/*  // }}*/}
      {/*  size={"small"}*/}
      {/*  placeholder={"Search"}*/}
      {/*  // disabled={isDataFetching}*/}
      {/*/>*/}
      <Tooltip title={"Filters"}>
        <span>
          <Button
            variant={"outlined"}
            endIcon={<FiltersIcon sx={{ width: "24px", height: "24px" }} />}
            size={"small"}
            onClick={handleOpenFilters}
          >
            Filters
            {extendedFiltersLength > 0 && (
              <Avatar
                sx={{
                  position: "absolute",
                  height: "16px",
                  width: "16px",
                  top: "2px",
                  right: "6px",
                  fontSize: "12px",
                  backgroundColor: theme.palette.secondary.main,
                  // border: `solid 1px ${theme.palette.secondary.main}`,
                  pointerEvents: "none",
                }}
              >
                {extendedFiltersLength}
              </Avatar>
            )}
          </Button>
        </span>
      </Tooltip>
      <FormControlLabel
        onChange={(e, checked) =>
          setFilters((prev) => ({
            ...prev,
            onlyUntrackedInPipeline: checked,
          }))
        }
        control={<Switch checked={filters.onlyUntrackedInPipeline} />}
        label={"Exclude Tracked Opportunities"}
        disabled={isDataFetching}
      />
    </Box>
  );

  const bottomLine = (
    <Box
      sx={{
        my: 1.5,
        display: "flex",
        gap: "16px",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ minWidth: 480, maxWidth: 600 }}>
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          options={AGENCY_OPTIONS}
          value={AGENCY_OPTIONS.filter((opt) => filters[opt.key])}
          getOptionLabel={(opt) => opt.label}
          filterOptions={agencyFilterOptions}
          isOptionEqualToValue={(a, b) => a.key === b.key}
          onChange={(_, selected) => {
            const selectedKeys = new Set(selected.map((s) => s.key));
            setFilters((prev) => ({
              ...prev,
              ...AGENCY_OPTIONS.reduce(
                (acc, opt) => ({
                  ...acc,
                  [opt.key]: selectedKeys.has(opt.key),
                }),
                {} as Record<AgencyFilterKey, boolean>,
              ),
            }));
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={selected}
                sx={{ mr: 1 }}
              />
              {`${option.fullName} (${option.label})`}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Agencies"
              placeholder="Select agencies"
            />
          )}
          ChipProps={{
            size: "small",
            sx: {
              "& .MuiChip-deleteIcon": {
                fontSize: "18px !important",
                width: "18px !important",
                height: "18px !important",
              },
            },
          }}
          disabled={isDataFetching}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          "& > *": { display: "flex", alignItems: "center" },
        }}
      >
        <Typography variant={"subtitle2"} color={"textSecondary"}>
          <SourceIcon
            color={"secondary"}
            sx={{
              height: "16px",
              width: "16px",
              mr: 0.5,
              mt: "-2px",
            }}
          />
          Source: grants.gov
        </Typography>
        <Typography variant={"subtitle2"} color={"textSecondary"}>
          <LastUpdatedIcon
            sx={{
              height: "16px",
              width: "16px",
              mr: 0.5,
              mt: "-2px",
            }}
          />
          Last updated: {lastUpdated ?? "N/A"}
        </Typography>
      </Box>
    </Box>
  );

  return [topLine, bottomLine];
};

export const getExportableDataTable = (
  data: SummaryGrant[],
  savedColumnSettings?: ColumnsSettingsData[] | null,
): TableProperties => {
  // Get default columns configuration (not details version)
  const defaultColumns = getGrantsColumnsConfig(
    getSortableColumnPropertyName,
    false,
  );

  // Get ordered and filtered columns based on user settings
  const orderedColumns = getOrderedExportColumns(
    savedColumnSettings,
    defaultColumns,
  );

  // Define data accessors for each column property
  const dataAccessors: {
    [propertyName: string]: (record: SummaryGrant) => any;
  } = {
    topLevelAgency: (record) => record.topLevelAgency,
    agencyName: (record) => record.record.agencyName,
    opportunityTitle: (record) => removeHtmlTags(record.opportunityTitle),
    description: (record) => removeHtmlTags(record.record.description || ""),
    status: (record) => record.record.status,
    manualLastUpdatedDate: (record) =>
      record.manualLastUpdatedDate
        ? formatDate(record.manualLastUpdatedDate, DateFormat.dd_MMMM_yyyy)
        : "",
    opportunityNumber: (record) => record.opportunityNumber,
    fundingInstrumentDescription: (record) =>
      record.record.fundingInstrumentDescription,
    opportunityCategoryDescription: (record) =>
      record.opportunityCategoryDescription,
    fundingActivityDescription: (record) =>
      record.record.fundingActivityDescription,
    numberOfAwards: (record) => record.record.numberOfAwards,
    cfdas: (record) => record.cfdas.join(", "),
    costSharing: (record) => (record.record.costSharing ? "True" : "False"),
    version: (record) => record.record.version,
    originalDueDate: (record) =>
      formatDate(record.originalDueDate, DateFormat.dd_MMMM_yyyy),
    responseDate: (record) =>
      formatDate(record.record.responseDate, DateFormat.dd_MMMM_yyyy),
    postingDate: (record) =>
      formatDate(record.record.postingDate, DateFormat.dd_MMMM_yyyy),
    archiveDate: (record) =>
      formatDate(record.record.archiveDate, DateFormat.dd_MMMM_yyyy),
    estimatedFundingFormatted: (record) =>
      `$${record.record.estimatedFundingFormatted}`,
    awardCeilingFormatted: (record) =>
      `$${record.record.awardCeilingFormatted}`,
    awardFloorFormatted: (record) => record.record.awardFloorFormatted,
    applicantTypeDescription: (record) =>
      record.record.applicantTypeDescription,
    applicantEligibilityDescription: (record) =>
      removeHtmlTags(record.record.applicantEligibilityDescription || ""),
    agencyContactName: (record) =>
      removeHtmlTags(record.record.agencyContactName || ""),
    agencyContactDescription: (record) =>
      removeHtmlTags(record.record.agencyContactDescription || ""),
    agencyContactEmail: (record) => record.record.agencyContactEmail,
    agencyContactPhone: (record) => record.record.agencyContactPhone,
  };

  // Add Grants.gov link as first column (maintaining backward compatibility)
  const hasSamGovColumn =
    !savedColumnSettings || savedColumnSettings.length === 0;

  let finalColumns = orderedColumns;
  let finalAccessors = { ...dataAccessors };

  if (hasSamGovColumn) {
    finalColumns = [
      {
        propertyName: "grantsGovLink",
        label: "Grants.gov link",
        order: -1,
        isVisible: true,
        sortable: false,
      } as DataColumnConfigProps,
      ...orderedColumns,
    ];
    finalAccessors["grantsGovLink"] = (record) =>
      record?.id
        ? `https://grants.gov/search-results-detail/${record?.id}`
        : "";
  }

  // Map columns and data to export format
  const { columns, rows } = mapColumnsToExportFormat(
    finalColumns,
    data,
    finalAccessors,
  );

  return buildExcelTable("GrantsExport", columns, rows);
};

export const getExcelData = async (
  context,
  queryDto,
  totalItems: number = 1000,
): Promise<ForecastRecord[]> => {
  const q = queryDto;
  if (q) {
    q.pageSize = totalItems;
    q.pageIndex = 0;
  }
  let result = Array<any>();
  await axios
    .get(
      getFullUrl(`/api/grant${buildQuery(q, "|")}`, {
        useDedicatedEnvironment: true,
      }),
      createRequestWithAuthHeaders(context),
    )
    .then((response) => {
      result = response.data.data;
    })
    .catch((error) => {
      alert("error fetching data");
    });
  return result;
};

export const getEmptyStringWithCurrencyIfNull = (
  d: string | null,
  defaultReturn?: string,
) => (d === null ? (defaultReturn ?? "") : `$${d}`);
